// src/pages/About.js
import React from "react";
import { Link } from "react-router-dom";
import "./About.css";

const VALUES = [
  {
    icon: "♻️",
    title: "Integrity Above All",
    desc: "We operate with honesty, transparency, and ethical discipline in every decision we make. Trust is the foundation of our relationships—with customers, partners, employees, and communities.",
  },
  {
    icon: "🏆",
    title: "Respect for Nature",
    desc: "We believe nature is not a resource to be exploited, but a system to be preserved. Our solutions are designed to work in harmony with natural processes, protecting ecosystems today and for generations to come.",
  },
  {
    icon: "💡",
    title: "Inclusive Growth",
    desc: "True progress is shared. We are committed to creating meaningful opportunities for youth, underserved communities, and those often left behind—uplifting the lowest, the last, and the lost through dignified work and skill development.",
  },
  {
    icon: "🤝",
    title: "Responsibility to Society",
    desc: "Giving back is not an initiative—it is a responsibility. We build businesses that contribute positively to society by strengthening local economies and enabling sustainable livelihoods.",
  },
  {
    icon: "🎯",
    title: "Purpose-Led Excellence",
    desc: "We pursue quality, innovation, and longterm value with discipline and intent. Every product and service we create is guided by purpose, performance, and accountability.",
  },
];

const TEAM_STATS = [
  { num: "100%", label: "Sustainable Products" },
  { num: "3+", label: "Niche Segments" },
  { num: "10+", label: "Countries" },
  { num: "∞", label: "Growth Potential" },
];

export default function About() {
  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div className="about-hero__bg" />
        <div className="container">
          <div className="about-hero__content">
            <span className="badge">🌿 Our Story</span>
            <h1 className="about-hero__title">
              We Are <em>Sunaharco</em>
            </h1>
            <p className="about-hero__subtitle">
              Dedicated to delivering trusted solutions across agriculture,
              premium ceramic mugs, and HVAC comfort systems—built around
              quality, sustainability, and customer-focused innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section about-story">
        <div className="container">
          <div className="about-story__inner">
            <span className="badge">🌿 Our Story</span>
            <div className="about-story__content">
              <p className="about-story__paragraph">
                Sunaharco was founded by lifelong friends whose journey began close to nature and evolved through years of professional global experience. Bound by shared values and a deep sense of responsibility, we came together with a singular purpose - to give back with intent and impact.
              </p>
              <p className="about-story__paragraph">
                After extensive international exposure to sustainable practices and disciplined business models, we returned with the conviction that meaningful enterprises can create both value and change. Sunaharco was established as a platform for <strong>high-quality, sustainable products and services</strong> that deliver enduring environmental, social, and economic returns.
              </p>
              <p className="about-story__paragraph">
                Sustainability and governance are integral to our operating philosophy. Our ESG principles guide every decision we make - from environmentally responsible solutions and ethical sourcing to strong, transparent governance frameworks.  At the heart of our mission lies a commitment to people: <strong>creating dignified employment opportunities for youth and underserved communities</strong>, building capabilities, and enabling livelihoods that strengthen local economies.
              </p>
              <p className="about-story__paragraph">
                Sunaharco stands for <strong>purpose-led growth</strong> - where sound strategy, social responsibility, and capital align to build resilient businesses and a more inclusive future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="section">
        <div className="container">
          <div className="about-split">
            <div className="about-split__text">
              <span className="badge">👋 Who We Are</span>
              <h2>A Brand Guided by Nature and Responsibility</h2>
              <p>
                Sunaharco is a diversified sustainability-driven brand built on
                the belief that long-term progress comes from working in harmony
                with nature. Our portfolio spans <strong>organic agricultural solutions
                and thoughtfully designed lifestyle products</strong>, each guided by
                quality, responsibility, and purpose.
              </p>
              <p>
                Our journey began with a simple yet powerful conviction: nature
                already holds the answers. By combining natural processes with
                modern insight, we create solutions that respect ecological
                balance while delivering reliable performance. From harnessing
                organic decomposition and earthworm-based systems to developing
                sustainable consumer offerings, our approach is rooted in
                regeneration—not extraction.
              </p>
              <p>
                Beyond products, Sunaharco is committed to impact. We operate
                with a strong ESG foundation—prioritizing environmental care,
                ethical sourcing, and inclusive growth. Central to who we are is
                our focus on <strong>creating meaningful employment for youth and
                underserved communities</strong>, fostering skills, and strengthening
                local economies.
              </p>
              <p>
                Sunaharco is where innovation meets responsibility—building
                sustainable solutions for today, while investing in resilience
                for the future.
              </p>
            </div>
            <div className="about-split__visual">
              <div className="about-stats-grid">
                {TEAM_STATS.map((s) => (
                  <div key={s.label} className="about-stat">
                    <span className="about-stat__num">{s.num}</span>
                    <span className="about-stat__label">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section section-alt">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card mv-card--mission">
              <div className="mv-card__icon">🎯</div>
              <h3>Our Mission</h3>
              <p>
                To create sustainable products and services that work in harmony
                with nature—delivering long term environmental, social, and
                economic value. We aim to empower farmers, consumers, and
                communities through responsible solutions, inclusive growth, and
                practices that support healthy ecosystems and resilient
                livelihoods.
              </p>
            </div>
            <div className="mv-card mv-card--vision">
              <div className="mv-card__icon">🔭</div>
              <h3>Our Vision</h3>
              <p>
                To be a trusted and principled force in sustainable solutions -
                guided by honesty, ethics, and a deep commitment to giving back
                to society. We envision a future where responsible business
                uplifts the lowest, the last, and the oftenlost, creating
                opportunity through dignified livelihoods and inclusive growth.
                Our vision is of a greener, healthier planet where ethical
                practices are nonnegotiable, ecosystems are respected, and
                progress is measured not only by success, but by the positive
                impact we leave on people, communities, and generations to come.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="badge">💎 What We Stand For</span>
            <h2 className="section-title">Our Core Values</h2>
          </div>
          <div className="values-grid">
            {VALUES.map((v) => (
              <div key={v.title} className="value-card">
                <span className="value-card__icon">{v.icon}</span>
                <h4 className="value-card__title">{v.title}</h4>
                <p className="value-card__desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="container">
          <h2>Ready to Grow Naturally?</h2>
          <p>
            Explore our products or get in touch to learn more about how
            Sunaharco can help you.
          </p>
          <div className="about-cta__actions">
            <Link to="/products" className="btn btn-gold">
              Explore Products
            </Link>
            <Link
              to="/contact"
              className="btn btn-outline"
              style={{ borderColor: "white", color: "white" }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
